import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useSiteSeo } from '@/lib/useSiteSeo';
import { Link } from 'react-router-dom';
import {
  Trophy, Gift, Calendar, MapPin, Users, ShieldCheck, ArrowRight, Star, Cpu,
} from 'lucide-react';
import FreeEntryForm from '@/components/competition/FreeEntryForm';
import SimRigEntryForm from '@/components/competition/SimRigEntryForm';
import PostalEntryInfo from '@/components/competition/PostalEntryInfo';
import CompetitionTerms from '@/components/competition/CompetitionTerms';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function Competition() {
  useSiteSeo('competition', {
    title: 'Win a Free Sim Racing Experience | Monthly Competition | Motion Reality',
    description: 'Enter the Motion Reality competition: a free monthly draw to win a sim racing experience, plus a £5 entry into our six-monthly Win a Sim Rig giveaway. Open to UK residents.',
  });

  // Show the paid sim rig tab when returning from Stripe with a payment status.
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get('paid') ? 'rig' : 'free');
  const [winners, setWinners] = useState([]);
  const [entryCount, setEntryCount] = useState(null);

  useEffect(() => {
    base44.entities.CompetitionEntry.filter({ is_winner: true, winner_announced: true }, '-winner_month', 6)
      .then((rows) => {
        setWinners((rows || []).map((w) => ({
          name: `${w.first_name} ${w.last_name?.charAt(0) || ''}.`.trim(),
          month: w.winner_month,
        })));
      })
      .catch(() => {});
    base44.entities.CompetitionEntry.list('-created_date', 1000)
      .then((rows) => setEntryCount(rows?.length ? rows.length : null))
      .catch(() => {});
  }, []);

  const currentMonth = MONTH_NAMES[new Date().getMonth()];
  const currentYear = new Date().getFullYear();
  const nextMonthName = MONTH_NAMES[(new Date().getMonth() + 1) % 12];
  const firstDrawPending = new Date() < new Date(2026, 8, 30, 23, 59, 59);

  return (
    <div className="min-h-screen bg-[#0d0d0f]">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-transparent" />
        <div className="absolute inset-0 carbon-bg opacity-40" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 pt-28 pb-16 lg:pt-36 lg:pb-24">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-5 h-5 text-primary" />
            <p className="text-primary text-xs font-bold tracking-widest">COMPETITION · FREE + PAID DRAWS</p>
          </div>
          <h1 className="text-white font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter leading-[0.95] max-w-3xl">
            Win a Free Experience<br />or a Full Sim Rig
          </h1>
          <p className="text-white/50 text-base sm:text-lg mt-5 max-w-xl leading-relaxed">
            Every month we give away a free sim racing session at our Bromborough venue. Plus, every six months one
            lucky winner takes home a complete pro-spec sim rig — enter for just £5.
          </p>
        </div>
      </section>

      {/* TABS */}
      <section className="border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-8">
          <div className="inline-flex border border-white/10">
            <button
              onClick={() => setTab('free')}
              className={`px-5 py-3 text-[11px] font-bold tracking-widest transition-colors ${tab === 'free' ? 'bg-primary text-white' : 'text-white/50 hover:text-white'}`}
            >
              FREE MONTHLY DRAW
            </button>
            <button
              onClick={() => setTab('rig')}
              className={`px-5 py-3 text-[11px] font-bold tracking-widest transition-colors border-l border-white/10 ${tab === 'rig' ? 'bg-primary text-white' : 'text-white/50 hover:text-white'}`}
            >
              WIN A SIM RIG · £5 ENTRY
            </button>
          </div>
        </div>
      </section>

      {/* FREE MONTHLY DRAW */}
      {tab === 'free' && (
        <>
          <section className="py-12 px-6 lg:px-10 border-b border-white/5">
            <div className="max-w-6xl mx-auto">
              <div className="grid sm:grid-cols-3 gap-4">
                {[
                  { icon: Users, title: '1. ENTER FREE', body: 'Fill in your details below. One entry per person per month — completely free, no purchase necessary.' },
                  { icon: Calendar, title: '2. AUTOMATED DRAW', body: `On the 1st of each month we randomly draw one winner from all ${currentMonth} entries. The more months you enter, the more chances.` },
                  { icon: Gift, title: '3. CLAIM YOUR PRIZE', body: 'The winner is emailed directly. Just reply to book your free session at a time that suits you.' },
                ].map((s, i) => (
                  <div key={i} className="border border-white/10 bg-[#111115] p-6">
                    <div className="w-10 h-10 bg-primary/10 border border-primary/30 flex items-center justify-center mb-4">
                      <s.icon className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="text-white font-bold text-sm tracking-widest mb-2">{s.title}</h3>
                    <p className="text-white/40 text-xs leading-relaxed">{s.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="enter" className="py-12 px-6 lg:px-10 border-b border-white/5">
            <div className="max-w-6xl mx-auto grid lg:grid-cols-5 gap-8">
              <div className="lg:col-span-3">
                <FreeEntryForm currentMonth={currentMonth} currentYear={currentYear} />
              </div>
              <div className="lg:col-span-2 space-y-4">
                <div className="border border-primary/30 bg-primary/[0.04] p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Trophy className="w-4 h-4 text-primary" />
                    <p className="text-primary text-[10px] font-bold tracking-widest">THIS MONTH'S DRAW</p>
                  </div>
                  {firstDrawPending ? (
                    <>
                      <p className="text-white font-black text-xl uppercase">First Draw · 30 Sep 2026</p>
                      <p className="text-white/40 text-xs mt-2 leading-relaxed">
                        First winner drawn 30 September 2026, then monthly on the 1st. Announced on our social channels.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-white font-black text-xl uppercase">{currentMonth} {currentYear}</p>
                      <p className="text-white/40 text-xs mt-2 leading-relaxed">
                        Winner drawn on the 1st of {nextMonthName} and announced on our social channels.
                      </p>
                    </>
                  )}
                  {entryCount != null && <p className="text-white/30 text-[11px] mt-3">{entryCount}+ entries all-time</p>}
                </div>
                <div className="border border-white/10 bg-[#111115] p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="w-4 h-4 text-white/50" />
                    <p className="text-white/50 text-[10px] font-bold tracking-widest">THE VENUE</p>
                  </div>
                  <p className="text-white/80 text-sm font-bold">Bromborough, Wirral</p>
                  <p className="text-white/40 text-xs mt-2 leading-relaxed">
                    Pro-spec direct-drive rigs, motion platforms and triple-screen setups. Just off the M53, minutes from Liverpool.
                  </p>
                  <Link to="/simulators" className="inline-flex items-center gap-1.5 text-primary hover:text-primary/80 text-xs font-bold tracking-widest mt-3">
                    SEE THE RIGS <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
                <div className="border border-white/10 bg-[#111115] p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <ShieldCheck className="w-4 h-4 text-white/50" />
                    <p className="text-white/50 text-[10px] font-bold tracking-widest">FAIR & FREE</p>
                  </div>
                  <ul className="space-y-2 text-white/40 text-xs">
                    <li className="flex items-start gap-2"><Star className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" /> Random draw, fully automated</li>
                    <li className="flex items-start gap-2"><Star className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" /> No purchase required</li>
                    <li className="flex items-start gap-2"><Star className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" /> Open to UK residents aged 5+</li>
                    <li className="flex items-start gap-2"><Star className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" /> Winner notified by email</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* SIM RIG DRAW */}
      {tab === 'rig' && (
        <section id="enter" className="py-12 px-6 lg:px-10 border-b border-white/5">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 mb-3">
                <Cpu className="w-5 h-5 text-primary" />
                <p className="text-primary text-xs font-bold tracking-widest">SIX-MONTHLY DRAW · £5 PER ENTRY</p>
              </div>
              <h2 className="text-white font-black text-3xl sm:text-4xl uppercase">Win a Full Sim Racing Rig</h2>
              <p className="text-white/50 text-sm mt-3 max-w-xl mx-auto">
                For every £5 entry you go into the draw to win a complete pro-spec sim racing rig. The more entries, the
                more chances — drawn at random every six months.
              </p>
            </div>
            <SimRigEntryForm />
            <div className="max-w-3xl mx-auto mt-8">
              <PostalEntryInfo />
            </div>
          </div>
        </section>
      )}

      {/* PREVIOUS WINNERS */}
      {winners.length > 0 && (
        <section className="py-16 px-6 lg:px-10 border-b border-white/5">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-white/30 text-[10px] font-bold tracking-widest mb-2">PREVIOUS WINNERS</p>
              <h2 className="text-white font-black text-2xl sm:text-3xl uppercase">Lucky Drivers</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {winners.map((w, i) => (
                <div key={i} className="border border-white/10 bg-[#111115] p-5 flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary/10 border border-primary/30 flex items-center justify-center shrink-0">
                    <Star className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm">{w.name}</p>
                    <p className="text-white/40 text-xs">{w.month}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TERMS & CONDITIONS */}
      <CompetitionTerms />

      {/* CTA */}
      <section className="py-16 px-6 lg:px-10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-white font-black text-2xl sm:text-3xl uppercase">Ready to win?</h2>
          <p className="text-white/50 text-sm mt-3 max-w-md mx-auto">Enter the free monthly draw every month, or grab a £5 sim rig entry.</p>
          <a href="#enter" onClick={() => setTab('free')} className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-6 py-3.5 transition-colors mt-6">
            ENTER NOW <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>
    </div>
  );
}